import { Button, Flex, Menu, Stack } from '@mantine/core'
import { Link, RichTextEditor, RichTextEditorControl } from '@mantine/tiptap'
import {
  IconAlignCenter,
  IconAlignJustified,
  IconAlignLeft,
  IconAlignRight,
} from '@tabler/icons-react'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import withErrorBoundaryAndPolicyCheck from '@v2/commons/HOC/ErrorBoundaryAndPolicyCheckHOC'
import { POLICIES } from '@v2/commons/config'

import styles from './add-message.module.scss'

interface AddNoteProps {
  onCancel: () => void
  onAddNote: (content: string) => void
  isLoading: boolean
}

function AddNote({ onAddNote, onCancel, isLoading }: AddNoteProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({
        placeholder: 'Add a new note...',
      }),
    ],
    content: '',
  })

  const handleAddNote = () => {
    const content = editor?.getHTML()
    if (!!content) {
      onAddNote(content)
      editor?.commands.clearContent()
    }
  }

  return (
    <Stack mt={32}>
      <RichTextEditor editor={editor} variant="subtle">
        <RichTextEditor.Toolbar
          sticky
          stickyOffset={60}
          className={styles['toolbar']}
        >
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Underline />
          <RichTextEditor.Strikethrough />
          <RichTextEditor.Blockquote />
          <RichTextEditor.Link />
          <RichTextEditor.Unlink />
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
          <Menu withinPortal>
            <Menu.Target>
              <RichTextEditorControl active={false}>
                <IconAlignLeft size={16} />
              </RichTextEditorControl>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconAlignLeft size={16} />}
                onClick={() =>
                  editor?.chain().focus().setTextAlign('left').run()
                }
              >
                Align Left
              </Menu.Item>
              <Menu.Item
                leftSection={<IconAlignCenter size={16} />}
                onClick={() =>
                  editor?.chain().focus().setTextAlign('center').run()
                }
              >
                Align Center
              </Menu.Item>
              <Menu.Item
                leftSection={<IconAlignRight size={16} />}
                onClick={() =>
                  editor?.chain().focus().setTextAlign('right').run()
                }
              >
                Align Right
              </Menu.Item>
              <Menu.Item
                leftSection={<IconAlignJustified size={16} />}
                onClick={() =>
                  editor?.chain().focus().setTextAlign('justify').run()
                }
              >
                Justify
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </RichTextEditor.Toolbar>
        <RichTextEditor.Content />
      </RichTextEditor>
      <Flex justify="space-between" mt={10} mb={10}>
        <Button
          variant="default"
          className={styles['button-cancel']}
          loading={isLoading}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          className={styles['button-save']}
          loading={isLoading}
          onClick={handleAddNote}
          disabled={!editor?.getText().trim()}
        >
          Add Note
        </Button>
      </Flex>
    </Stack>
  )
}

export default withErrorBoundaryAndPolicyCheck(
  AddNote,
  POLICIES.isGetChatMessagesEnabled,
)
